import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import {
  backtestFilterDefaultValues,
  BacktestFilterType,
  BacktestSortType,
  useApplicationSuiteContext,
} from '../../../contexts/ApplicationSuiteProvider'
import { Box, useMediaQuery } from '@mui/material'
import BacktestPanelContainer from '../../../layout/BacktestPanelContainer'
import { useTheme } from '@mui/material/styles'
import BacktestEdit from '../../../components/backtests/BacktestEdit'
import { Tabs } from '../../../layout/MainLayout'
import useBacktestFolders, { BacktestFolder } from '../../../hooks/useBacktestFolders'
import { FolderMembershipContext } from './FolderMembershipContext'

const backtestSortDefaultValue: BacktestSortType = {
  field: 'summary.grades.grade',
  direction: 'desc',
}

type BacktestType = 'featured' | 'showcase' | 'favorites' | 'user'

// LocalStorage keys
const SHOWCASE_FILTERS_KEY = 'showcase_filters'
const SHOWCASE_SORT_KEY = 'showcase_sort'
const SHOWCASE_TYPE_KEY = 'showcase_type'

interface ShowcaseContextProps {
  backtestFilters: BacktestFilterType
  setBacktestFilters: React.Dispatch<React.SetStateAction<BacktestFilterType>>
  backtestSort: BacktestSortType
  setBacktestSort: React.Dispatch<React.SetStateAction<BacktestSortType>>
  type: BacktestType
  setType: React.Dispatch<React.SetStateAction<BacktestType>>
  focusedBacktest: any
  setFocusedBacktest: React.Dispatch<React.SetStateAction<any>>
  folders: BacktestFolder[]
  foldersLoading: boolean
  createFolder: (name: string) => Promise<void>
  renameFolder: (folderId: string, name: string) => Promise<void>
  deleteFolder: (folderId: string) => Promise<void>
  moveBacktestToFolder: (backtestId: string, targetFolderId: string | null) => Promise<void>
}

// Create the contexts
const ShowcaseContext = createContext<ShowcaseContextProps | undefined>(undefined)

interface Props {
  children: ReactNode
  isNested?: boolean
  parentTab?: Tabs
}

// Helper function to get stored value from localStorage
const getStoredValue = <T,>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue

  try {
    const item = window.localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error)
    return defaultValue
  }
}

export const ShowcaseProvider: React.FC<Props> = ({ children, isNested, parentTab }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { tradingHouse, activeTab, backtestToView, setBacktestToView, usermeta } =
    useApplicationSuiteContext()

  // Initialize state with values from localStorage or defaults
  const [backtestFilters, setBacktestFilters] = useState<BacktestFilterType>(
    getStoredValue(SHOWCASE_FILTERS_KEY, backtestFilterDefaultValues)
  )
  const [backtestSort, setBacktestSort] = useState<BacktestSortType>(
    getStoredValue(SHOWCASE_SORT_KEY, backtestSortDefaultValue)
  )
  const [type, setType] = useState<BacktestType>(() => {
    const storedType = getStoredValue<BacktestType | null>(SHOWCASE_TYPE_KEY, null)
    return storedType ?? 'featured'
  })
  const [focusedBacktest, setFocusedBacktest] = useState<any>(null)

  // Owned here (rather than by ShowcaseBacktestReports) so BacktestPanelContainer
  // — a sibling under this provider, not a child of ShowcaseBacktestReports — can
  // also reach folder data for its own move-to-folder action.
  const {
    folders,
    loading: foldersLoading,
    createFolder,
    renameFolder,
    deleteFolder,
    moveBacktestToFolder,
  } = useBacktestFolders(usermeta?.gcid, type === 'favorites')

  // Wrapper functions that sync with localStorage
  const _setBacktestFilters: React.Dispatch<React.SetStateAction<BacktestFilterType>> = (value) => {
    const newValue = value instanceof Function ? value(backtestFilters) : value
    setBacktestFilters(newValue)
    localStorage.setItem(SHOWCASE_FILTERS_KEY, JSON.stringify(newValue))
  }

  const _setBacktestSort: React.Dispatch<React.SetStateAction<BacktestSortType>> = (value) => {
    const newValue = value instanceof Function ? value(backtestSort) : value
    setBacktestSort(newValue)
    localStorage.setItem(SHOWCASE_SORT_KEY, JSON.stringify(newValue))
  }

  const _setType: React.Dispatch<React.SetStateAction<BacktestType>> = (value) => {
    const newValue = value instanceof Function ? value(type) : value
    setType(newValue)
    localStorage.setItem(SHOWCASE_TYPE_KEY, JSON.stringify(newValue))
  }

  useEffect(() => {
    if (backtestToView) {
      setFocusedBacktest(backtestToView)
    }
  }, [backtestToView])

  useEffect(() => {
    setFocusedBacktest(null)
  }, [tradingHouse])

  const panelFolders = type === 'favorites' ? folders : undefined
  const panelOnMoveToFolder =
    type === 'favorites'
      ? (folderId: string | null) => moveBacktestToFolder(focusedBacktest.id, folderId)
      : undefined

  return (
    <ShowcaseContext.Provider
      value={{
        backtestFilters,
        setBacktestFilters: _setBacktestFilters,
        backtestSort,
        setBacktestSort: _setBacktestSort,
        type,
        setType: _setType,
        focusedBacktest,
        setFocusedBacktest,
        folders,
        foldersLoading,
        createFolder,
        renameFolder,
        deleteFolder,
        moveBacktestToFolder,
      }}
    >
      <FolderMembershipContext.Provider value={{ folders, moveBacktestToFolder }}>
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              // if backtestToView is set, put display to none, otherwise display flex
              display: backtestToView ? 'none' : 'flex',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Main content area */}
            <Box
              sx={{
                overflowY: 'auto',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {children}
            </Box>

            {!isMobile && activeTab.tab === parentTab && (
              <Box
                sx={{
                  flexShrink: 0,
                  overflowY: 'auto',
                  background: theme.palette.background.paper,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  position: isNested ? 'absolute' : 'relative',
                  top: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <BacktestPanelContainer
                  backtest={focusedBacktest}
                  onClose={() => setFocusedBacktest(null)}
                  onView={() => setBacktestToView(focusedBacktest)}
                  folders={panelFolders}
                  onMoveToFolder={panelOnMoveToFolder}
                />
              </Box>
            )}

            {/* For mobile, render outside the flex container to overlay properly */}
            {isMobile && activeTab.tab === parentTab && (
              <BacktestPanelContainer
                backtest={focusedBacktest}
                onClose={() => setFocusedBacktest(null)}
                onView={() => setBacktestToView(focusedBacktest)}
                folders={panelFolders}
                onMoveToFolder={panelOnMoveToFolder}
              />
            )}
          </Box>
          {backtestToView && (
            <BacktestEdit backtestToView={backtestToView} goBack={() => setBacktestToView(null)} />
          )}
        </Box>
      </FolderMembershipContext.Provider>
    </ShowcaseContext.Provider>
  )
}

// Custom hook to use this contexts
export const useShowcaseContext = (): ShowcaseContextProps => {
  const context = useContext(ShowcaseContext)
  if (!context) {
    throw new Error('useShowcaseContext must be used within a ShowcaseProvider')
  }
  return context
}

export type { BacktestType }
